import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import './AdminPage.css';

function AdminPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [boards, setBoards] = useState([]);
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);

  // 모달 state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(''); // 'member', 'post', 'comment'
  const [selectedItem, setSelectedItem] = useState(null);
  const [editData, setEditData] = useState({});

  // 검색 state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('all');

  // eslint-disable-next-line no-unused-vars
  const boardNames = {
    'notice': '알림판',
    'bible_index': '성서마당',
    'theo_index': '신학마당',
    'current': '세상읽기',
    'free': '사랑채',
    'sermon': '설교모음',
    'mark': '매일묵상',
    'churchs': '교회소식',
    'member': '후원안내',
    // 섹션별 이름
    'bible': '성서연구',
    'comment': '설교비평',
    'berg': '판넨베르크',
    'study1': '특강',
    'study2': '온라인 강의실',
    // 칼럼니스트
    'jung': '정용섭 칼럼',
    'kim': '김기석 칼럼',
    'lee': '이정배 칼럼'
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.is_admin !== 'Y') {
      alert('관리자 권한이 필요합니다.');
      navigate('/');
      return;
    }
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchTerm('');
    setSearchType('all');
    switch (activeTab) {
      case 'stats': fetchStats(); break;
      case 'members': fetchMembers(); break;
      case 'boards': fetchBoards(); break;
      case 'posts': fetchPosts(); break;
      case 'comments': fetchComments(); break;
      default: break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchWithAuth = async (url, options = {}) => {
    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    if (response.status === 401 || response.status === 403) {
      alert('권한이 없거나 로그인이 만료되었습니다.');
      navigate('/login');
      throw new Error('Unauthorized');
    }
    return response.json();
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_BASE_URL}/api/admin/stats`);
      setStats(data);
    } catch (error) {
      console.error('Stats fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_BASE_URL}/api/admin/members`);
      setMembers(data);
    } catch (error) {
      console.error('Members fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoards = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_BASE_URL}/api/admin/boards`);
      setBoards(data);
    } catch (error) {
      console.error('Boards fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async (searchParams = {}) => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/api/admin/posts`;
      const queryParts = [];
      if (searchParams.keyword) {
        queryParts.push(`keyword=${encodeURIComponent(searchParams.keyword)}`);
        queryParts.push(`type=${searchParams.type || 'all'}`);
      }
      if (queryParts.length > 0) {
        url += '?' + queryParts.join('&');
      }

      const data = await fetchWithAuth(url);
      setPosts(data);
    } catch (error) {
      console.error('Posts fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (searchParams = {}) => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/api/admin/comments`;
      const queryParts = [];
      if (searchParams.keyword) {
        queryParts.push(`keyword=${encodeURIComponent(searchParams.keyword)}`);
        queryParts.push(`type=${searchParams.type || 'all'}`);
      }
      if (queryParts.length > 0) {
        url += '?' + queryParts.join('&');
      }

      const data = await fetchWithAuth(url);
      setComments(data);
    } catch (error) {
      console.error('Comments fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (activeTab === 'posts') {
      fetchPosts({ keyword: searchTerm, type: searchType });
    } else if (activeTab === 'comments') {
      fetchComments({ keyword: searchTerm, type: searchType });
    }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('이 게시글을 삭제하시겠습니까?')) return;
    try {
      await fetchWithAuth(`${API_BASE_URL}/api/admin/post/${id}`, { method: 'DELETE' });
      alert('삭제되었습니다.');
      fetchPosts();
    } catch (error) {
      alert('삭제 실패');
    }
  };

  const handleDeleteComment = async (id) => {
    if (!window.confirm('이 댓글을 삭제하시겠습니까?')) return;
    try {
      await fetchWithAuth(`${API_BASE_URL}/api/admin/comment/${id}`, { method: 'DELETE' });
      alert('삭제되었습니다.');
      fetchComments();
    } catch (error) {
      alert('삭제 실패');
    }
  };

  const handleToggleMemberStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'Y' ? 'N' : 'Y';
    try {
      await fetchWithAuth(`${API_BASE_URL}/api/admin/member/${id}/deny`, {
        method: 'PUT',
        body: JSON.stringify({ denied: newStatus })
      });
      alert('회원 상태가 변경되었습니다.');
      fetchMembers();
    } catch (error) {
      alert('상태 변경 실패');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const y = dateStr.substring(0, 4);
    const m = dateStr.substring(4, 6);
    const d = dateStr.substring(6, 8);
    const h = dateStr.substring(8, 10);
    const min = dateStr.substring(10, 12);
    return `${y}.${m}.${d} ${h}:${min}`;
  };

  // HTML 태그 제거 함수
  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  };

  // 모달 열기
  const openModal = (type, item) => {
    setModalType(type);
    setSelectedItem(item);
    setEditData(item);
    setModalOpen(true);
  };

  // 모달 닫기
  const closeModal = () => {
    setModalOpen(false);
    setModalType('');
    setSelectedItem(null);
    setEditData({});
  };

  // 수정 데이터 변경
  const handleEditChange = (field, value) => {
    setEditData({ ...editData, [field]: value });
  };

  // 회원 정보 수정
  const handleUpdateMember = async () => {
    try {
      await fetchWithAuth(`${API_BASE_URL}/api/admin/member/${selectedItem.member_srl}`, {
        method: 'PUT',
        body: JSON.stringify({
          nick_name: editData.nick_name,
          email_address: editData.email,
          is_admin: editData.is_admin,
          denied: editData.denied,
          new_password: editData.new_password // Send new password if set
        })
      });
      alert('회원 정보가 수정되었습니다.');
      closeModal();
      fetchMembers();
    } catch (error) {
      alert('수정 실패');
    }
  };

  // 게시글 수정
  const handleUpdatePost = async () => {
    try {
      await fetchWithAuth(`${API_BASE_URL}/api/admin/post/${selectedItem.document_srl}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: editData.title,
          content: editData.content || selectedItem.content,
          status: editData.status
        })
      });
      alert('게시글이 수정되었습니다.');
      closeModal();
      fetchPosts();
    } catch (error) {
      alert('수정 실패');
    }
  };

  // 댓글 수정
  const handleUpdateComment = async () => {
    try {
      await fetchWithAuth(`${API_BASE_URL}/api/admin/comment/${selectedItem.comment_srl}`, {
        method: 'PUT',
        body: JSON.stringify({
          content: editData.content
        })
      });
      alert('댓글이 수정되었습니다.');
      closeModal();
      fetchComments();
    } catch (error) {
      alert('수정 실패');
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>🔐 관리자 페이지</h1>
      </div>

      <div className="admin-navigation">
        <button className={`nav-tab ${activeTab === 'stats' ? 'active' : ''}`} onClick={() => setActiveTab('stats')}>통계</button>
        <button className={`nav-tab ${activeTab === 'members' ? 'active' : ''}`} onClick={() => setActiveTab('members')}>회원관리</button>
        <button className={`nav-tab ${activeTab === 'boards' ? 'active' : ''}`} onClick={() => setActiveTab('boards')}>게시판관리</button>
        <button className={`nav-tab ${activeTab === 'posts' ? 'active' : ''}`} onClick={() => setActiveTab('posts')}>게시글관리</button>
        <button className={`nav-tab ${activeTab === 'comments' ? 'active' : ''}`} onClick={() => setActiveTab('comments')}>댓글관리</button>
      </div>

      <div className="admin-content">
        {loading ? <div className="loading">로딩 중...</div> : (
          <>
            {activeTab === 'stats' && stats && (
              <div className="stats-section">
                <h2>사이트 통계</h2>
                <div className="stats-grid">
                  <div className="stat-card"><div className="stat-label">전체 회원</div><div className="stat-value">{stats.members}명</div></div>
                  <div className="stat-card"><div className="stat-label">전체 게시판</div><div className="stat-value">{stats.boards}개</div></div>
                  <div className="stat-card"><div className="stat-label">전체 게시글</div><div className="stat-value">{stats.posts}개</div></div>
                  <div className="stat-card"><div className="stat-label">전체 댓글</div><div className="stat-value">{stats.comments}개</div></div>
                </div>
              </div>
            )}

            {activeTab === 'members' && (
              <div className="table-section">
                <h2>회원 목록 ({members.length})</h2>
                <table className="admin-table">
                  <thead>
                    <tr><th>ID</th><th>닉네임</th><th>이메일</th><th>가입일</th><th>상태</th><th>관리</th></tr>
                  </thead>
                  <tbody>
                    {members.map(m => (
                      <tr key={m.member_srl}>
                        <td>{m.user_id}</td><td>{m.nick_name}</td><td>{m.email}</td>
                        <td>{formatDate(m.regdate)}</td>
                        <td>{m.denied === 'Y' ? '정지' : '정상'}</td>
                        <td>
                          <button onClick={() => openModal('member', m)}>상세보기</button>
                          <button onClick={() => handleToggleMemberStatus(m.member_srl, m.denied)}>
                            {m.denied === 'Y' ? '해제' : '정지'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'boards' && (
              <div className="table-section">
                <h2>게시판 목록 ({boards.length})</h2>
                <table className="admin-table">
                  <thead>
                    <tr><th>ID</th><th>게시판 이름</th><th>생성일</th></tr>
                  </thead>
                  <tbody>
                    {boards.map(b => (
                      <tr key={b.module_srl}>
                        <td>{b.mid}</td><td>{b.title}</td><td>{formatDate(b.regdate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'posts' && (
              <div className="table-section">
                <h2>게시글 목록 ({posts.length})</h2>
                <form onSubmit={handleSearch} style={{ marginBottom: '10px' }}>
                  <select value={searchType} onChange={(e) => setSearchType(e.target.value)} style={{ padding: '5px', marginRight: '5px' }}>
                    <option value="all">전체</option>
                    <option value="title">제목</option>
                    <option value="content">내용</option>
                    <option value="author">작성자</option>
                  </select>
                  <input
                    type="text"
                    placeholder="검색어 입력"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ padding: '5px', marginRight: '5px' }}
                  />
                  <button type="submit" style={{ padding: '5px 10px' }}>검색</button>
                </form>
                <table className="admin-table">
                  <thead>
                    <tr><th>제목</th><th>작성자</th><th>게시판</th><th>조회</th><th>작성일</th><th>관리</th></tr>
                  </thead>
                  <tbody>
                    {posts.map(p => (
                      <tr key={p.document_srl}>
                        <td>{p.title}</td><td>{p.author}</td><td>{p.board_id}</td>
                        <td>{p.readed_count}</td><td>{formatDate(p.regdate)}</td>
                        <td>
                          <button onClick={() => openModal('post', p)}>상세보기</button>
                          <button onClick={() => handleDeletePost(p.document_srl)}>삭제</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'comments' && (
              <div className="table-section">
                <h2>댓글 목록 ({comments.length})</h2>
                <form onSubmit={handleSearch} style={{ marginBottom: '10px' }}>
                  <select value={searchType} onChange={(e) => setSearchType(e.target.value)} style={{ padding: '5px', marginRight: '5px' }}>
                    <option value="all">전체</option>
                    <option value="content">내용</option>
                    <option value="author">작성자</option>
                  </select>
                  <input
                    type="text"
                    placeholder="검색어 입력"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ padding: '5px', marginRight: '5px' }}
                  />
                  <button type="submit" style={{ padding: '5px 10px' }}>검색</button>
                </form>
                <table className="admin-table">
                  <thead>
                    <tr><th>내용</th><th>작성자</th><th>작성일</th><th>관리</th></tr>
                  </thead>
                  <tbody>
                    {comments.map(c => (
                      <tr key={c.comment_srl}>
                        <td>{stripHtml(c.content).substring(0, 50)}...</td><td>{c.author}</td>
                        <td>{formatDate(c.regdate)}</td>
                        <td>
                          <button onClick={() => openModal('comment', c)}>상세보기</button>
                          <button onClick={() => handleDeleteComment(c.comment_srl)}>삭제</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* 모달 */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modalType === 'member' && '회원 상세정보'}
                {modalType === 'post' && '게시글 상세정보'}
                {modalType === 'comment' && '댓글 상세정보'}
              </h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="modal-body">
              {modalType === 'member' && selectedItem && (
                <div className="form-group">
                  <label>아이디</label>
                  <input type="text" value={editData.user_id || ''} disabled />

                  <label>닉네임</label>
                  <input
                    type="text"
                    value={editData.nick_name || ''}
                    onChange={(e) => handleEditChange('nick_name', e.target.value)}
                  />

                  <label>이메일</label>
                  <input
                    type="email"
                    value={editData.email || ''}
                    onChange={(e) => handleEditChange('email', e.target.value)}
                  />

                  <label>관리자 권한</label>
                  <select
                    value={editData.is_admin || 'N'}
                    onChange={(e) => handleEditChange('is_admin', e.target.value)}
                  >
                    <option value="N">일반 회원</option>
                    <option value="Y">관리자</option>
                  </select>

                  <label>계정 상태</label>
                  <select
                    value={editData.denied || 'N'}
                    onChange={(e) => handleEditChange('denied', e.target.value)}
                  >
                    <option value="N">정상</option>
                    <option value="Y">정지</option>
                  </select>

                  <label>가입일</label>
                  <input type="text" value={formatDate(editData.regdate) || ''} disabled />

                  <label>마지막 로그인</label>
                  <input type="text" value={formatDate(editData.last_login) || ''} disabled />

                  <label>비밀번호 변경 (선택사항)</label>
                  <input
                    type="password"
                    placeholder="변경할 비밀번호를 입력하세요"
                    value={editData.new_password || ''}
                    onChange={(e) => handleEditChange('new_password', e.target.value)}
                  />
                </div>
              )}

              {modalType === 'post' && selectedItem && (
                <div className="form-group">
                  <label>게시글 번호</label>
                  <input type="text" value={editData.document_srl || ''} disabled />

                  <label>제목</label>
                  <input
                    type="text"
                    value={editData.title || ''}
                    onChange={(e) => handleEditChange('title', e.target.value)}
                  />

                  <label>작성자</label>
                  <input type="text" value={editData.author || ''} disabled />

                  <label>게시판</label>
                  <input type="text" value={editData.board_id || ''} disabled />

                  <label>상태</label>
                  <select
                    value={editData.status || 'PUBLIC'}
                    onChange={(e) => handleEditChange('status', e.target.value)}
                  >
                    <option value="PUBLIC">공개</option>
                    <option value="SECRET">비공개</option>
                    <option value="TRASH">휴지통</option>
                  </select>

                  <label>조회수</label>
                  <input type="text" value={editData.readed_count || 0} disabled />

                  <label>작성일</label>
                  <input type="text" value={formatDate(editData.regdate) || ''} disabled />
                </div>
              )}

              {modalType === 'comment' && selectedItem && (
                <div className="form-group">
                  <label>댓글 번호</label>
                  <input type="text" value={editData.comment_srl || ''} disabled />

                  <label>내용</label>
                  <textarea
                    rows="5"
                    value={editData.content || ''}
                    onChange={(e) => handleEditChange('content', e.target.value)}
                  />

                  <label>작성자</label>
                  <input type="text" value={editData.author || ''} disabled />

                  <label>게시글 번호</label>
                  <input type="text" value={editData.document_srl || ''} disabled />

                  <label>작성일</label>
                  <input type="text" value={formatDate(editData.regdate) || ''} disabled />
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeModal}>취소</button>
              <button className="btn-save" onClick={() => {
                if (modalType === 'member') handleUpdateMember();
                else if (modalType === 'post') handleUpdatePost();
                else if (modalType === 'comment') handleUpdateComment();
              }}>
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
